import { Injectable, signal, computed } from '@angular/core';
import { StrapiService } from '../dealers/strapi.service';
import { lastValueFrom } from 'rxjs';
import { VehicleModel } from './vehicles.component';
import { PublicPageDataService } from 'app/core/services/public-page-data.service';

@Injectable({
    providedIn: 'root'
})
export class VehiclesService {
    // Signal to store all vehicle models
    vehicleModels = signal<VehicleModel[]>([]);

    // Signal to track loading state
    isLoading = signal<boolean>(false);

    // Signal to track if data has been loaded
    private isDataLoaded = signal<boolean>(false);

    // Computed signal to get specific vehicle by ID
    getVehicleById = (id: string) => computed(() =>
        this.vehicleModels().find(model => model.id === id)
    );

    constructor(
        private _strapiService: StrapiService,
        private publicPageDataService: PublicPageDataService
    ) { }

    /**
     * Loads vehicle models from API if not already loaded
     * Uses cached data from StrapiService
     */
    async loadVehicleModels(): Promise<void> {
        // If data is already loaded, don't reload
        if (this.isDataLoaded()) {
            return;
        }

        try {
            this.isLoading.set(true);
            const models = await lastValueFrom(this.publicPageDataService.getVehiclesPageData());
            this.setVehicleModels(models || []);
        } catch (error) {
            console.error('Error loading vehicle models in VehiclesService:', error);
        } finally {
            this.isLoading.set(false);
        }
    }

    /**
     * Forces a reload of vehicle models from API
     * Useful for refreshing data
     */
    async reloadVehicleModels(): Promise<void> {
        this.isDataLoaded.set(false);
        await this.loadVehicleModels();
    }

    /**
     * Gets a vehicle model by ID
     */
    getVehicleModelById(id: string): VehicleModel | undefined {
        return this.vehicleModels().find(model => model.id === id);
    }

    /**
     * Clears all vehicle data
     */
    clearData(): void {
        this.vehicleModels.set([]);
        this.isDataLoaded.set(false);
    }

    setVehicleModels(models: VehicleModel[]): void {
        this.vehicleModels.set(models || []);
        this.isDataLoaded.set(Array.isArray(models) && models.length > 0);
    }
}
